import type { GuidedReportIntent } from '@aytm/lib/skipper/assistants/common/types'

// Feature token gating guided-report generation. Single point of change here.
export const SKIPPER_REPORT_FEATURE = 'skipper_guided_report' as const

export const CREATE_BUTTON_LABEL = 'Create new Skipper Report'
export const UNLOCK_BUTTON_FALLBACK_LABEL = 'Unlock Skipper Report'
// The chat's Guided Reports action pill. Its two gates are the button's own: the survey-level
// Skipper Reports flag decides whether it is offered, the fielding state whether it is live.
export const GUIDED_REPORT_PILL_LABEL = 'Customize my Skipper Report'
export const SURVEY_IN_FIELD_TOOLTIP = 'Available once the survey finishes fielding'
// The paywall control takes the same fielding gate as the Create button, and says the other half
// of it: what waits is the purchase, not the action it unlocks.
export const PURCHASE_IN_FIELD_TOOLTIP = 'Available for purchase once the survey finishes fielding'
// Hover callout on the Create button while the addon is free during Beta (gated on the
// backend-supplied `beta` flag on the ai_assistants feature).
export const BETA_FREE_LABEL = 'Free while in Beta'
// A report that has not generated yet carries no panel string and no resolved spec. It is the one
// empty state the section still renders for: everything else with nothing to show is withheld.
export const ANALYSIS_SETTINGS_PENDING = 'Analysis settings will appear here once Skipper has generated this report.'
// Sticky notice raised when a generation ends in failure. Matches the note the failed report
// itself carries, so the live page and the report body do not state the outcome two ways.
export const GUIDED_REPORT_ERROR_NOTICE =
  "We ran into a problem generating your Skipper Report. Please reach out to support and we'll get it sorted out."
// Shown when the survey is at the saved-report ceiling, which every add path honours.
export const REPORTS_CAP_TOOLTIP = 'This survey has reached its limit of saved reports. Delete one to make room.'
// Shown when the survey already has the max in-flight guided reports generating.
export const CAP_REACHED_TOOLTIP =
  'You already have 3 reports generating for this survey. Wait for one to finish before requesting another.'

// Story-3 banner copy (spec-final, from guided_reports_spec §2 Story 3 "Copy (final)"). The
// estimate is 5-10 min since parallelism landed; the proposal's older 10-15 was superseded.
export const BANNER_HEADLINE = 'Create a tailored Skipper Report for any audience or question'
export const BANNER_BODY =
  'Tell Skipper the angle, segment, or objective in the chat — it builds the full report for you. ' +
  'Generation takes ~5–10 min, and you can keep working while it runs.'

// Entry-intent sent structurally on the message-send (guided_report.intent), distinct
// from the seed text below so Skipper is told what the user asked for, not which UI
// affordance was used. Consumed by Skipper::Chat::Stats (ENTRY_INTENTS).
export const CREATE_REPORT_INTENT: GuidedReportIntent = 'create_report'
export const MODIFY_REPORT_INTENT: GuidedReportIntent = 'modify_report'

// Intent seed messages for the chat-entry seam (Phase-1 entry is by chat intent).
export const SEED_CREATE_NEW = 'I want to create a new tailored Skipper Report for this survey. Help me set it up.'
// reportId names the report in the text the user sees; the binding itself rides the entry signal.
export const SEED_CHANGE_REPORT = (reportName: string, reportId: number): string =>
  `I want to change the Skipper Report "${reportName}" (report #${reportId}). Show me its current parameters so I can edit them.`

// Fixed DOM id the chat button mounts under (set by lib/skipper/ChatButton/index.tsx).
export const CHAT_BUTTON_ROOT_ID = 'skipper-chat-button-root'
export const CHAT_SIDEBAR_OPEN_CLASS = 'chat-sidebar-open'
